import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from "react";
import { LOC_STORAGE } from "@vahak/core/dist/constants/misc";
import { getLocalStorageData, setLocalStorageData } from "../../methods/localStorage";
import { CallInitiationParams } from "../../services/call/useCallInitiation";
import { CallRequiredMeta, MpcClickToCallProvider, useMpcClickToCall } from "./BrowerClickToCallProviderMpc";
import { SingleClickToCallProvider, useSingleClickToCall } from "./BrowerClickToCallProviderOld";
import { SharedPlivoSdkProvider } from "./SharedPlivoSdkProvider";

export type { CallRequiredMeta, CallInfo, MemberInfo } from "./BrowerClickToCallProviderMpc";

interface CallModeContextType {
    isMpcMode: boolean;
    setIsMpcMode: (enabled: boolean) => void;
    toggleMpcMode: () => void;
}

type ClickToCallApi = {
    startCall: (meta: CallRequiredMeta, callInitiateParams: CallInitiationParams) => void;
    registerCallCta: (meta: CallRequiredMeta, callInitiateParams: CallInitiationParams) => void;
    isReady?: boolean;
    isCallSessionActive?: boolean;
    callCtaStateKey?: string;
};

const CallModeContext = createContext<CallModeContextType | undefined>(undefined);

const getInitialMpcMode = (): boolean => {
    if (typeof window === "undefined") {
        return true;
    }

    const stored = getLocalStorageData<boolean | Record<string, never>>(LOC_STORAGE.clickToCallMpcMode);
    return typeof stored === "boolean" ? stored : false;
};

const UnifiedClickToCallContext = createContext<ClickToCallApi | undefined>(undefined);

const MpcClickToCallBridge = ({ onApiChange }: { onApiChange: (api: ClickToCallApi | undefined) => void }) => {
    const api = useMpcClickToCall();
    const onApiChangeRef = useRef(onApiChange);
    const apiRef = useRef(api);

    onApiChangeRef.current = onApiChange;
    apiRef.current = api;

    useLayoutEffect(() => {
        onApiChangeRef.current(apiRef.current);
        return () => onApiChangeRef.current(undefined);
    }, [api?.isReady, api?.isCallSessionActive, api?.callCtaStateKey]);

    useLayoutEffect(() => {
        onApiChangeRef.current(apiRef.current);
    }, []);

    return null;
};

const SingleClickToCallBridge = ({ onApiChange }: { onApiChange: (api: ClickToCallApi | undefined) => void }) => {
    const api = useSingleClickToCall();
    const onApiChangeRef = useRef(onApiChange);
    const apiRef = useRef(api);

    onApiChangeRef.current = onApiChange;
    apiRef.current = api;

    useLayoutEffect(() => {
        onApiChangeRef.current(apiRef.current);
        return () => onApiChangeRef.current(undefined);
    }, [api?.isReady, api?.isCallSessionActive, api?.callCtaStateKey]);

    useLayoutEffect(() => {
        onApiChangeRef.current(apiRef.current);
    }, []);

    return null;
};

export const ClickToCallAppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isMpcMode, setIsMpcModeState] = useState(getInitialMpcMode);
    const [unifiedApi, setUnifiedApi] = useState<ClickToCallApi | undefined>();
    const isCallSessionActiveRef = useRef(false);
    isCallSessionActiveRef.current = Boolean(unifiedApi?.isCallSessionActive);

    const handleApiChange = useCallback((api: ClickToCallApi | undefined) => {
        setUnifiedApi((prev) => {
            if (!api) {
                return undefined;
            }
            if (!prev) {
                return api;
            }
            if (prev.isReady !== api.isReady) {
                return api;
            }
            if (prev.isCallSessionActive !== api.isCallSessionActive) {
                return api;
            }
            if (prev.callCtaStateKey !== api.callCtaStateKey) {
                return api;
            }
            if (prev.startCall !== api.startCall || prev.registerCallCta !== api.registerCallCta) {
                return api;
            }
            return prev;
        });
    }, []);

    const setIsMpcMode = useCallback((enabled: boolean) => {
        if (isCallSessionActiveRef.current) {
            return;
        }
        setIsMpcModeState((current) => (current === enabled ? current : enabled));
    }, []);

    const toggleMpcMode = useCallback(() => {
        if (isCallSessionActiveRef.current) {
            return;
        }
        setIsMpcModeState((current) => !current);
    }, []);

    useEffect(() => {
        setLocalStorageData(LOC_STORAGE.clickToCallMpcMode, isMpcMode);
    }, [isMpcMode]);

    const callModeValue = useMemo(
        () => ({
            isMpcMode,
            setIsMpcMode,
            toggleMpcMode
        }),
        [isMpcMode, setIsMpcMode, toggleMpcMode]
    );

    return (
        <CallModeContext.Provider value={callModeValue}>
            <SharedPlivoSdkProvider>
                <UnifiedClickToCallContext.Provider value={unifiedApi}>
                    {isMpcMode ? (
                        <MpcClickToCallProvider key="mpc">
                            <MpcClickToCallBridge onApiChange={handleApiChange} />
                        </MpcClickToCallProvider>
                    ) : (
                        <SingleClickToCallProvider key="single">
                            <SingleClickToCallBridge onApiChange={handleApiChange} />
                        </SingleClickToCallProvider>
                    )}
                    {children}
                </UnifiedClickToCallContext.Provider>
            </SharedPlivoSdkProvider>
        </CallModeContext.Provider>
    );
};

export const useCallMode = (): CallModeContextType => {
    const context = useContext(CallModeContext);
    if (!context) {
        throw new Error("useCallMode must be used within ClickToCallAppProvider");
    }
    return context;
};

export const useClickToCall = () => useContext(UnifiedClickToCallContext);

export const CallBtnWithIcon = (props: { meta: CallRequiredMeta; callInitiateParams: CallInitiationParams }) => {
    const context = useClickToCall();
    const CTA_SLOT = context?.registerCallCta(props.meta, props.callInitiateParams);
    return <>{CTA_SLOT}</>;
};
